/**
 * People IQ — Public candidate onboarding form (NO LOGIN required).
 * Reached via a one-time, expiring link emailed by HR:
 *   /people/invite/<token>
 *
 * Everything the candidate submits lands in the HR Approval Queue as a
 * pending change request (channel=candidate_form). Nothing touches the
 * master record until HR approves.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const DOC_TYPES = [
  { key: "identity_proof", label: "Identity proof (PAN / Aadhaar)" },
  { key: "address_proof", label: "Address proof" },
  { key: "education_proof", label: "Education certificate" },
  { key: "bank_proof", label: "Bank proof (passbook / cancelled cheque)" },
  { key: "blood_group_proof", label: "Blood Group Proof (mandatory)" },
  { key: "family_photograph", label: "Family Photo (For ESIC)" },
  { key: "other", label: "Other" },
];

// Sep 2026 — blood group is a fixed dropdown, never free text.
const BLOOD_GROUPS = [
  { v: "O+", l: "O positive (O+)" }, { v: "O-", l: "O negative (O-)" },
  { v: "A+", l: "A positive (A+)" }, { v: "A-", l: "A negative (A-)" },
  { v: "B+", l: "B positive (B+)" }, { v: "B-", l: "B negative (B-)" },
  { v: "AB+", l: "AB positive (AB+)" }, { v: "AB-", l: "AB negative (AB-)" },
];

export default function OnboardingInvite() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [docs, setDocs] = useState([]); // {title, doc_type, uploaded filename}
  const [form, setForm] = useState({
    preferred_name: "", personal_email: "", date_of_birth: "", gender: "",
    marital_status: "", personal_mobile: "", blood_group: "",
    present_line1: "", present_city: "", present_state: "", present_pincode: "",
    permanent_same: true,
    permanent_line1: "", permanent_city: "", permanent_state: "", permanent_pincode: "",
    emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relation: "",
    pan: "", aadhaar: "", voter_id: "", driving_license: "",
    esic_number: "", uan_number: "",
    bank_account_number: "", bank_ifsc: "", bank_name: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/people/onboarding-form/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.detail || "Invalid link");
        setMeta(data);
        // Prefill any known values
        setForm((f) => ({
          ...f,
          personal_email: data.employee.personal_email || "",
          preferred_name: data.employee.preferred_name || "",
          personal_mobile: data.employee.personal_mobile || "",
          date_of_birth: data.employee.date_of_birth || "",
        }));
      } catch (e) {
        setError(e.message);
      }
    })();
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const uploadDoc = async (docType, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      const res = await fetch(`${API}/api/people/onboarding-form/${token}/documents`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      setDocs((d) => [...d, data]);
      toast.success(`${file.name} uploaded`);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const submit = async () => {
    if (!form.pan && !form.aadhaar) return toast.error("Please provide PAN or Aadhaar (at least one is required)");
    setSaving(true);
    try {
      const payload = {};
      const simple = [
        "preferred_name", "personal_email", "date_of_birth", "gender", "marital_status",
        "personal_mobile", "blood_group",
        "emergency_contact_name", "emergency_contact_phone", "emergency_contact_relation",
        "pan", "aadhaar", "voter_id", "driving_license", "esic_number", "uan_number",
        "bank_account_number", "bank_ifsc", "bank_name",
      ];
      simple.forEach((k) => { if (form[k]) payload[k] = form[k]; });
      // Statutory numbers apply only when HR has marked the record eligible.
      if (!meta.employee.esic_applicable) delete payload.esic_number;
      if (!meta.employee.pf_applicable) delete payload.uan_number;
      if (form.present_line1 || form.present_city) {
        payload.present_address = {
          line1: form.present_line1, city: form.present_city,
          state: form.present_state, pincode: form.present_pincode, country: "India",
        };
      }
      if (!form.permanent_same && (form.permanent_line1 || form.permanent_city)) {
        payload.permanent_address = {
          line1: form.permanent_line1, city: form.permanent_city,
          state: form.permanent_state, pincode: form.permanent_pincode, country: "India",
        };
      } else if (payload.present_address) {
        payload.permanent_address = payload.present_address;
      }

      const res = await fetch(`${API}/api/people/onboarding-form/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submission failed");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.message);
    } finally { setSaving(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center" data-testid="invite-error">
          <div className="text-4xl mb-3">⚠</div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-sm text-slate-600">{error}</p>
          <p className="text-xs text-slate-400 mt-4">If you believe this is a mistake, please contact HR for a fresh link.</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow p-8 max-w-md text-center" data-testid="invite-success">
          <div className="w-14 h-14 rounded-full bg-emerald-100 text-emerald-600 text-2xl flex items-center justify-center mx-auto mb-3">✓</div>
          <h1 className="text-lg font-bold text-slate-900 mb-2">Details submitted</h1>
          <p className="text-sm text-slate-600">
            Thank you, {meta?.employee?.full_name}. HR will verify your details and complete your onboarding.
            You'll be contacted if anything further is needed.
          </p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="min-h-screen bg-slate-100 flex items-center justify-center text-slate-400 text-sm">Loading your onboarding form…</div>;
  }

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="onboarding-invite-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-xs font-bold tracking-widest uppercase text-[#7C1D3A]">BBS Global Pvt. Ltd.</div>
          <h1 className="text-2xl font-bold text-slate-900 mt-1">Employee Onboarding Form</h1>
          <p className="text-sm text-slate-500 mt-1">
            Hello <b>{meta.employee.full_name}</b> (Code <b>{meta.employee.employee_code}</b>).
            Please fill in your details below. HR will verify them before your record is finalized.
            This link expires on <b>{(meta.expires_at || "").slice(0, 10)}</b>.
          </p>
        </div>

        <Section title="Personal details">
          <Grid2>
            <L label="Preferred name"><I value={form.preferred_name} onChange={(v) => set("preferred_name", v)} testid="cand-preferred" /></L>
            <L label="Personal email *"><I type="email" value={form.personal_email} onChange={(v) => set("personal_email", v)} testid="cand-email" /></L>
            <L label="Date of birth"><I type="date" value={form.date_of_birth} onChange={(v) => set("date_of_birth", v)} testid="cand-dob" /></L>
            <L label="Personal mobile *"><I value={form.personal_mobile} onChange={(v) => set("personal_mobile", v)} testid="cand-mobile" /></L>
            <L label="Gender">
              <select value={form.gender} onChange={(e) => set("gender", e.target.value)} className={cls} data-testid="cand-gender">
                <option value="">—</option><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option>
              </select>
            </L>
            <L label="Marital status">
              <select value={form.marital_status} onChange={(e) => set("marital_status", e.target.value)} className={cls} data-testid="cand-marital">
                <option value="">—</option><option value="single">Single</option><option value="married">Married</option>
                <option value="divorced">Divorced</option><option value="widowed">Widowed</option>
              </select>
            </L>
            <L label="Blood group">
              <select value={form.blood_group} onChange={(e) => set("blood_group", e.target.value)} className={cls} data-testid="cand-blood">
                <option value="">—</option>
                {BLOOD_GROUPS.map((b) => <option key={b.v} value={b.v}>{b.l}</option>)}
              </select>
            </L>
          </Grid2>
        </Section>

        <Section title="Addresses">
          <Grid2>
            <L label="Present address — line"><I value={form.present_line1} onChange={(v) => set("present_line1", v)} testid="cand-present-line1" /></L>
            <L label="City"><I value={form.present_city} onChange={(v) => set("present_city", v)} /></L>
            <L label="State"><I value={form.present_state} onChange={(v) => set("present_state", v)} /></L>
            <L label="Pincode"><I value={form.present_pincode} onChange={(v) => set("present_pincode", v)} maxLength={6} /></L>
          </Grid2>
          <label className="flex items-center gap-2 mt-3 text-sm text-slate-700">
            <input type="checkbox" checked={form.permanent_same} onChange={(e) => set("permanent_same", e.target.checked)} data-testid="cand-perm-same" />
            Permanent address same as present
          </label>
          {!form.permanent_same && (
            <Grid2 className="mt-3">
              <L label="Permanent address — line"><I value={form.permanent_line1} onChange={(v) => set("permanent_line1", v)} /></L>
              <L label="City"><I value={form.permanent_city} onChange={(v) => set("permanent_city", v)} /></L>
              <L label="State"><I value={form.permanent_state} onChange={(v) => set("permanent_state", v)} /></L>
              <L label="Pincode"><I value={form.permanent_pincode} onChange={(v) => set("permanent_pincode", v)} maxLength={6} /></L>
            </Grid2>
          )}
        </Section>

        <Section title="Emergency contact">
          <Grid2>
            <L label="Name"><I value={form.emergency_contact_name} onChange={(v) => set("emergency_contact_name", v)} testid="cand-ec-name" /></L>
            <L label="Phone"><I value={form.emergency_contact_phone} onChange={(v) => set("emergency_contact_phone", v)} testid="cand-ec-phone" /></L>
            <L label="Relationship"><I value={form.emergency_contact_relation} onChange={(v) => set("emergency_contact_relation", v)} placeholder="spouse / father / …" /></L>
          </Grid2>
        </Section>

        <Section title="Identity & statutory">
          <Grid2>
            <L label="PAN"><I value={form.pan} onChange={(v) => set("pan", v.toUpperCase())} maxLength={10} placeholder="ABCDE1234F" testid="cand-pan" /></L>
            <L label="Aadhaar (12 digits)"><I value={form.aadhaar} onChange={(v) => set("aadhaar", v.replace(/[^\d]/g, ""))} maxLength={12} testid="cand-aadhaar" /></L>
            <L label="Voter ID"><I value={form.voter_id} onChange={(v) => set("voter_id", v.toUpperCase())} /></L>
            <L label="Driving license"><I value={form.driving_license} onChange={(v) => set("driving_license", v)} /></L>
            {meta.employee.esic_applicable ? (
              <L label="ESIC number"><I value={form.esic_number} onChange={(v) => set("esic_number", v)} testid="cand-esic" /></L>
            ) : (
              <L label="ESIC number"><input value="Not applicable" disabled className={cls + " bg-slate-100 text-slate-400 italic"} data-testid="cand-esic-na" /></L>
            )}
            {meta.employee.pf_applicable ? (
              <L label="PF / UAN Number"><I value={form.uan_number} onChange={(v) => set("uan_number", v)} testid="cand-uan" /></L>
            ) : (
              <L label="PF / UAN Number"><input value="Not applicable" disabled className={cls + " bg-slate-100 text-slate-400 italic"} data-testid="cand-uan-na" /></L>
            )}
          </Grid2>
        </Section>

        <Section title="Bank details">
          <Grid2>
            <L label="Account number"><I value={form.bank_account_number} onChange={(v) => set("bank_account_number", v.replace(/[^\d]/g, ""))} testid="cand-bank-acc" /></L>
            <L label="IFSC"><I value={form.bank_ifsc} onChange={(v) => set("bank_ifsc", v.toUpperCase())} testid="cand-bank-ifsc" /></L>
            <L label="Bank name"><I value={form.bank_name} onChange={(v) => set("bank_name", v)} /></L>
          </Grid2>
        </Section>

        <Section title="Documents">
          <p className="text-xs text-slate-500 mb-3">
            Upload clear scans/photos of your documents (PDF, JPG, PNG — max 15 MB each).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {DOC_TYPES.filter((d) => d.key !== "family_photograph" || meta.employee.esic_applicable).map((d) => (
              <div key={d.key} className="border border-slate-200 rounded-md p-3">
                <div className="text-sm font-medium text-slate-800">{d.label}</div>
                <div className="text-[11px] text-slate-400 mb-2">{docs.filter((x) => x.doc_type === d.key).map((x) => x.title).join(", ") || "Not uploaded"}</div>
                <label className="inline-block text-xs bg-slate-800 text-white rounded px-3 py-1.5 cursor-pointer" data-testid={`cand-doc-${d.key}`}>
                  Choose file
                  <input type="file" className="hidden" onChange={(e) => e.target.files?.[0] && uploadDoc(d.key, e.target.files[0])} />
                </label>
              </div>
            ))}
          </div>
        </Section>

        <div className="bg-white rounded-lg shadow p-5 sticky bottom-3 flex items-center justify-between gap-3">
          <p className="text-[11px] text-slate-500 max-w-sm">
            By submitting, you confirm these details are accurate. HR will review before updating your record. This link is single-use.
          </p>
          <button
            onClick={submit}
            disabled={saving}
            className="bg-[#7C1D3A] hover:bg-[#6c1c36] disabled:opacity-50 text-white font-semibold text-sm rounded-md px-6 py-2.5"
            data-testid="cand-submit"
          >
            {saving ? "Submitting…" : "Submit for HR review"}
          </button>
        </div>
      </div>
    </div>
  );
}

const cls = "w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white";

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 mb-4">{title}</h2>
      {children}
    </div>
  );
}

function Grid2({ children, className = "" }) {
  return <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${className}`}>{children}</div>;
}

function L({ label, children }) {
  return (
    <div>
      <label className="text-[11px] uppercase font-semibold tracking-wider text-slate-500">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function I({ value, onChange, type = "text", placeholder, maxLength, testid }) {
  return (
    <input
      type={type}
      value={value}
      placeholder={placeholder}
      maxLength={maxLength}
      onChange={(e) => onChange(e.target.value)}
      className={cls}
      data-testid={testid}
    />
  );
}
